import React from 'react';
import { Book, FileText, Layout, Film, Mail, Terminal, Layers, Archive, CheckCircle2, Sparkles } from 'lucide-react';

export const OutputShowcase: React.FC = () => {
  const outputs = [
    {
      title: 'Ebook Principal (PDF & EPUB)',
      tag: 'Formato Duplo',
      desc: 'Diagramação com tipografia equilibrada, índice navegável, numeração e formato EPUB para leitores digitais.',
      icon: Book,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Workbook & Caderno Prático',
      tag: 'Didática Ativa',
      desc: 'Material complementar com matrizes de decisão, exercícios práticos e frameworks para aplicação do aluno.',
      icon: FileText,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Capa & Identidade Visual',
      tag: 'Design 2D',
      desc: 'Arte de capa alinhada ao posicionamento do produto, pronta para vitrines de plataformas digitais.',
      icon: Layout,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Roteiro de VSL (3 Atos)',
      tag: 'Vídeo Script',
      desc: 'Roteiro de vendas com gancho inicial, mecanismo único, tese e chamada para ação pronta para locução.',
      icon: Film,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Sequência de E-mails',
      tag: 'Nutrição & Vendas',
      desc: 'Série estratégica cobrindo conscientização do problema, revelação da solução e quebra de objeções.',
      icon: Mail,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Copy para Página de Vendas',
      tag: 'Conversão',
      desc: 'Estrutura completa de copywriting com promessa, público ideal, módulos explicados e garantias.',
      icon: Layers,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Biblioteca de Prompts',
      tag: 'Ferramental',
      desc: 'Coleção de instruções e prompts de aplicação prática entregues ao leitor como acelerador de execução.',
      icon: Terminal,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Carrosséis Educativos',
      tag: 'Engenharia Social',
      desc: 'Roteiros de 7 a 10 slides desenhados para extrair pílulas de autoridade e alimentar canais de aquisição.',
      icon: Sparkles,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      title: 'Pacote ZIP Consolidado',
      tag: 'Entrega Final',
      desc: 'Arquivo único compactado contendo todos os ativos organizados em pastas, com hash SHA-256 de integridade.',
      icon: Archive,
      badge: 'Certificado',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    }
  ];

  return (
    <section id="entregaveis" className="py-20 md:py-28 bg-brand-surface/40 border-t border-brand-border relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-secondary px-3 py-1 rounded-full bg-brand-secondary/10 border border-brand-secondary/20">
            Catálogo de Entregáveis
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 mb-5 tracking-tight">
            Tudo o Que Seu Infoproduto Precisa Para Ir a Mercado
          </h2>
          <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
            Nada de rascunhos em branco ou arquivos soltos. O InfoForge entrega os artefatos compilados, validados e prontos para distribuição.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {outputs.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-brand-surface border border-brand-border hover:border-brand-primary/40 transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-mono text-brand-textSubtle uppercase tracking-wider">
                      {item.tag}
                    </span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                  </div>

                  <div className="w-10 h-10 rounded-xl bg-brand-bg border border-brand-border/60 flex items-center justify-center text-brand-cyan mb-4 group-hover:scale-105 transition-transform">
                    <Icon className="w-5 h-5" />
                  </div>

                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-brand-cyan transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-brand-textMuted leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-brand-border/40 flex items-center gap-2 text-[11px] text-emerald-400 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Pronto para download e distribuição
                </div>
              </div>
            );
          })}
        </div>

        {/* Honest Transparency Notice */}
        <div className="mt-12 p-5 rounded-xl bg-brand-bg/90 border border-brand-border/80 text-xs text-brand-textSubtle flex flex-col sm:flex-row items-center justify-between gap-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-brand-secondary" />
            <span>
              <strong>Transparência Operacional:</strong> Funcionalidades avançadas de vídeo generativo e voz neural estão em ciclo de certificação laboratorial e serão liberadas em marcos posteriores do roadmap.
            </span>
          </div>
          <span className="shrink-0 text-brand-cyan font-mono text-[11px]">
            Roadmap 2026
          </span>
        </div>
      </div>
    </section>
  );
};
